/**
 * Share-invitation Kind discriminator + sealed template snapshot contract
 * (supplier self-assessment slice 1, tracker #52; design D6: the Kind
 * discriminator lands early so `Kind = 'self_assessment'` rides the existing
 * share envelope without a later breaking contract change).
 *
 * - 'finding' is the existing supplier-collaboration share and stays the
 *   default for every legacy row and every create that does not name a kind.
 * - 'self_assessment' sends an audit template to a supplier as a
 *   questionnaire. The share EMBEDS the sealed template package at create
 *   (design key property 1: a snapshot, never a live cross-tenant template
 *   read), stored in ShareInvitation.TemplateSnapshotBlob (V085).
 *
 * The snapshot shape mirrors the SPFx `templatePackaging.ts` export format
 * (formatVersion 1). The two projects have no shared package, so — like the
 * template-library shelf — the structural contract is declared portal-side
 * as well; the customer plane remains the authority on template content.
 * Pure module: no SQL, no express.
 */

import { ValidationError } from '../../errors';

export type ShareInvitationKind = 'finding' | 'self_assessment';

export const SHARE_INVITATION_KINDS: ShareInvitationKind[] = ['finding', 'self_assessment'];

export const DEFAULT_SHARE_INVITATION_KIND: ShareInvitationKind = 'finding';

/** Matches the SPFx templatePackaging TEMPLATE_PACKAGE_FORMAT_VERSION. */
export const TEMPLATE_SNAPSHOT_FORMAT_VERSION = 1;

/**
 * Generous ceiling for an embedded sealed template package: the SPFx item
 * cap (500 items) keeps real packages far below this; the cap only guards
 * the licensing database against abuse of the blob column.
 */
export const TEMPLATE_SNAPSHOT_MAX_CHARS = 2 * 1024 * 1024;

/** Normalize a requested share kind; blank means the legacy default. */
export const normalizeShareInvitationKind = (value: unknown): ShareInvitationKind => {
  const kind = String(value ?? '').trim().toLowerCase();
  if (!kind) {
    return DEFAULT_SHARE_INVITATION_KIND;
  }
  if (!SHARE_INVITATION_KINDS.includes(kind as ShareInvitationKind)) {
    throw new ValidationError('Unsupported share invitation kind', 'COLLABORATION_SHARE_KIND_INVALID');
  }
  return kind as ShareInvitationKind;
};

/** Tolerant read-side mapping: legacy rows without the column are findings. */
export const shareInvitationKindFromRow = (value: string | null | undefined): ShareInvitationKind => {
  const kind = String(value || '').trim().toLowerCase();
  return SHARE_INVITATION_KINDS.includes(kind as ShareInvitationKind)
    ? (kind as ShareInvitationKind)
    : DEFAULT_SHARE_INVITATION_KIND;
};

/**
 * Validate and canonicalise the sealed template snapshot for a share create.
 *
 * - kind 'finding': a snapshot is not part of the contract — reject it so a
 *   caller cannot smuggle template content onto ordinary shares.
 * - kind 'self_assessment': a snapshot is REQUIRED. Accepts the package as
 *   an object or a JSON string; enforces the structural minimum of the SPFx
 *   package format (formatVersion 1, templateKey, title, non-empty items)
 *   without duplicating the full SPFx parser — the customer plane sealed the
 *   template and remains the enforcement point for content rules.
 *
 * Returns the canonical JSON string for TemplateSnapshotBlob (null for
 * 'finding').
 */
export const parseTemplateSnapshotForShareKind = (
  kind: ShareInvitationKind,
  snapshot: unknown
): string | null => {
  const provided = snapshot !== undefined && snapshot !== null && String(snapshot) !== '';
  if (kind !== 'self_assessment') {
    if (provided) {
      throw new ValidationError(
        'A template snapshot is only valid on self-assessment shares.',
        'COLLABORATION_TEMPLATE_SNAPSHOT_KIND_MISMATCH'
      );
    }
    return null;
  }
  if (!provided) {
    throw new ValidationError(
      'A sealed template snapshot is required for self-assessment shares.',
      'COLLABORATION_TEMPLATE_SNAPSHOT_REQUIRED'
    );
  }

  let parsed: unknown = snapshot;
  if (typeof snapshot === 'string') {
    if (snapshot.length > TEMPLATE_SNAPSHOT_MAX_CHARS) {
      throw new ValidationError(
        'The template snapshot exceeds the embedded size limit.',
        'COLLABORATION_TEMPLATE_SNAPSHOT_TOO_LARGE'
      );
    }
    try {
      parsed = JSON.parse(snapshot);
    } catch {
      throw new ValidationError(
        'The template snapshot is not valid JSON.',
        'COLLABORATION_TEMPLATE_SNAPSHOT_INVALID'
      );
    }
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new ValidationError(
      'The template snapshot must be a template package object.',
      'COLLABORATION_TEMPLATE_SNAPSHOT_INVALID'
    );
  }
  const pack = parsed as {
    formatVersion?: unknown;
    templateKey?: unknown;
    title?: unknown;
    items?: unknown;
  };
  if (pack.formatVersion !== TEMPLATE_SNAPSHOT_FORMAT_VERSION) {
    throw new ValidationError(
      `Unsupported template snapshot format version ${String(pack.formatVersion)}.`,
      'COLLABORATION_TEMPLATE_SNAPSHOT_INVALID'
    );
  }
  if (typeof pack.templateKey !== 'string' || !pack.templateKey.trim()) {
    throw new ValidationError(
      'The template snapshot needs a templateKey.',
      'COLLABORATION_TEMPLATE_SNAPSHOT_INVALID'
    );
  }
  if (typeof pack.title !== 'string' || !pack.title.trim()) {
    throw new ValidationError(
      'The template snapshot needs a title.',
      'COLLABORATION_TEMPLATE_SNAPSHOT_INVALID'
    );
  }
  if (!Array.isArray(pack.items) || pack.items.length === 0) {
    throw new ValidationError(
      'The template snapshot has no checklist items.',
      'COLLABORATION_TEMPLATE_SNAPSHOT_INVALID'
    );
  }

  const canonical = JSON.stringify(parsed);
  if (canonical.length > TEMPLATE_SNAPSHOT_MAX_CHARS) {
    throw new ValidationError(
      'The template snapshot exceeds the embedded size limit.',
      'COLLABORATION_TEMPLATE_SNAPSHOT_TOO_LARGE'
    );
  }
  return canonical;
};

/** The snapshot's templateKey; used to cross-check against the share payload. */
export const templateSnapshotTemplateKey = (templateSnapshotJson: string): string => {
  try {
    const parsed = JSON.parse(templateSnapshotJson) as { templateKey?: unknown };
    return typeof parsed.templateKey === 'string' ? parsed.templateKey.trim() : '';
  } catch {
    return '';
  }
};

/**
 * Supplier self-assessment lifecycle (tracker #52 slice 4). The customer's
 * share and inbox views surface sent / opened / submitted only (design §5:
 * lifecycle states, never keystrokes), with the share's terminal states
 * (revoked / expired) still winning when they apply. Pure derivation from
 * the invitation's stamps so every surface renders the same state.
 */
export type SelfAssessmentLifecycleState = 'sent' | 'opened' | 'submitted' | 'revoked' | 'expired';

export const selfAssessmentLifecycleOf = (invitation: {
  status: string;
  expiresAt: string;
  selfAssessmentFirstOpenedAtUtc?: string | null;
  selfAssessmentSubmittedAtUtc?: string | null;
}): SelfAssessmentLifecycleState => {
  const status = String(invitation.status || '').trim().toLowerCase();
  if (status === 'revoked') {
    return 'revoked';
  }
  if (invitation.selfAssessmentSubmittedAtUtc) {
    return 'submitted';
  }
  const expiresAtMs = new Date(invitation.expiresAt).getTime();
  if (status === 'expired' || (Number.isFinite(expiresAtMs) && expiresAtMs <= Date.now())) {
    return 'expired';
  }
  return invitation.selfAssessmentFirstOpenedAtUtc ? 'opened' : 'sent';
};
